/**
 * Callbacks Tab — ניהול בקשות "אבדוק עם יפתח"
 * Shows pending callbacks created when AI needs advisor input
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Phone, CheckCircle, Clock, MessageSquare, RefreshCw, AlertTriangle } from 'lucide-react'
import api from '../../services/api'

interface Callback {
  _id: string
  userId: string
  conversationId: string
  topic: string
  context: string
  status: 'pending' | 'done'
  advisorNotes?: string
  resolvedAt?: string
  createdAt: string
}

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

/** Human-readable Hebrew wait duration, e.g. "3 ימים", "5 שעות", "12 דק׳" */
function formatWait(ms: number): string {
  if (ms < HOUR_MS) return `${Math.max(1, Math.round(ms / 60000))} דק׳`
  if (ms < DAY_MS) return `${Math.floor(ms / HOUR_MS)} שע׳`
  return `${Math.floor(ms / DAY_MS)} ימים`
}

export default function CallbacksTab() {
  const [callbacks, setCallbacks] = useState<Callback[]>([])
  const [pendingList, setPendingList] = useState<Callback[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'pending' | 'done'>('pending')
  const [resolvingId, setResolvingId] = useState<string | null>(null)
  const [notes, setNotes] = useState('')

  const fetchCallbacks = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/chat/callbacks', { params: { status: filter } })
      setCallbacks(res.data?.callbacks || [])
    } catch {
      setCallbacks([])
    } finally {
      setLoading(false)
    }
  }, [filter])

  // Always keep the pending backlog loaded — powers the "ממתינות" summary
  // regardless of which status filter is currently selected.
  const fetchPending = useCallback(async () => {
    try {
      const res = await api.get('/chat/callbacks', { params: { status: 'pending' } })
      setPendingList(res.data?.callbacks || [])
    } catch {
      setPendingList([])
    }
  }, [])

  const refresh = useCallback(() => {
    fetchCallbacks()
    fetchPending()
  }, [fetchCallbacks, fetchPending])

  useEffect(() => {
    fetchCallbacks()
  }, [fetchCallbacks])

  useEffect(() => {
    fetchPending()
  }, [fetchPending])

  const handleResolve = async (id: string) => {
    try {
      await api.post(`/chat/callbacks/${id}/resolve`, { notes })
      setResolvingId(null)
      setNotes('')
      refresh()
    } catch (err) {
      console.error('Failed to resolve callback:', err)
    }
  }

  // Waiting-backlog summary — computed from the always-loaded pending list
  const summary = useMemo(() => {
    const now = Date.now()
    const ages = pendingList
      .map(cb => now - new Date(cb.createdAt).getTime())
      .filter(ms => Number.isFinite(ms) && ms >= 0)
      .sort((a, b) => b - a)
    const count = ages.length
    return {
      count,
      oldestMs: count ? ages[0] : 0,
      avgMs: count ? ages.reduce((s, ms) => s + ms, 0) / count : 0,
      over24h: ages.filter(ms => ms >= DAY_MS).length,
      over3d: ages.filter(ms => ms >= 3 * DAY_MS).length,
      over7d: ages.filter(ms => ms >= 7 * DAY_MS).length,
    }
  }, [pendingList])

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString('he-IL') + ' ' + d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-bold text-gray-900">
          בקשות חזרה — "אבדוק עם יפתח"
        </h2>
        <div className="flex items-center gap-3">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'pending' | 'done')}
            aria-label="סינון בקשות לפי סטטוס"
            className="input text-sm py-1.5"
          >
            <option value="pending">ממתינות</option>
            <option value="done">טופלו</option>
          </select>
          <button onClick={refresh} className="btn btn-outline text-sm p-2" title="רענן" aria-label="רענן">
            <RefreshCw size={16} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Waiting-backlog summary — כמה בקשות ממתינות וכמה זמן */}
      {summary.count > 0 ? (
        <div className="card mb-6 bg-amber-50/40 border border-amber-200">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            {/* Open count */}
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-amber-700">{summary.count}</span>
              <div className="text-sm text-gray-600 leading-tight">
                <div>בקשות ממתינות לטיפול</div>
              </div>
            </div>

            <div className="h-8 w-px bg-amber-200 hidden sm:block" />

            {/* Wait times */}
            <div className="text-sm text-gray-700 leading-relaxed">
              <div>
                <Clock size={13} className="inline ml-1 text-amber-500" aria-hidden="true" />
                הוותיקה ביותר ממתינה <span className="font-semibold">{formatWait(summary.oldestMs)}</span>
              </div>
              <div className="text-gray-500">
                זמן המתנה ממוצע: <span className="font-medium">{formatWait(summary.avgMs)}</span>
              </div>
            </div>

            <div className="h-8 w-px bg-amber-200 hidden sm:block" />

            {/* Aging buckets */}
            <div>
              <div className="text-xs text-gray-400 mb-1">לפי זמן המתנה</div>
              <div className="flex flex-wrap gap-1.5">
                <span className="text-xs font-medium px-2 py-0.5 rounded-full text-gray-600 bg-gray-100">
                  מעל 24 שע׳ {summary.over24h}
                </span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${summary.over3d > 0 ? 'text-orange-700 bg-orange-100' : 'text-gray-600 bg-gray-100'}`}>
                  מעל 3 ימים {summary.over3d}
                </span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${summary.over7d > 0 ? 'text-red-700 bg-red-100' : 'text-gray-600 bg-gray-100'}`}>
                  מעל שבוע {summary.over7d}
                </span>
              </div>
            </div>

            {summary.over3d > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-orange-700 bg-orange-100 rounded-full px-2.5 py-1">
                <AlertTriangle size={13} aria-hidden="true" />
                יש בקשות שממתינות זמן רב
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="card mb-6 bg-green-50/40 border border-green-200">
          <div className="flex items-center gap-2 text-sm text-green-700">
            <CheckCircle size={18} aria-hidden="true" />
            אין בקשות ממתינות — הכול טופל
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner w-8 h-8" />
        </div>
      ) : callbacks.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <CheckCircle size={40} className="mx-auto text-green-400 mb-3" aria-hidden="true" />
          <p className="text-gray-500">
            {filter === 'pending' ? 'אין בקשות ממתינות' : 'אין בקשות שטופלו'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {callbacks.map((cb) => (
            <div
              key={cb._id}
              className={`bg-white border rounded-xl p-5 ${
                cb.status === 'pending' ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  {cb.status === 'pending' ? (
                    <Clock size={18} className="text-amber-500" aria-hidden="true" />
                  ) : (
                    <CheckCircle size={18} className="text-green-500" aria-hidden="true" />
                  )}
                  <span className="font-medium text-gray-900 text-sm">
                    {cb.status === 'pending' ? 'ממתין לטיפול' : 'טופל'}
                  </span>
                  {cb.status === 'pending' && (
                    <span className="text-xs font-medium text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                      ממתין {formatWait(Date.now() - new Date(cb.createdAt).getTime())}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-400">{formatDate(cb.createdAt)}</span>
              </div>

              <div className="mb-3">
                <p className="text-sm font-medium text-gray-700 mb-1">נושא:</p>
                <p className="text-sm text-gray-600">{cb.topic}</p>
              </div>

              <div className="mb-3 bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-500 mb-1">
                  <MessageSquare size={12} className="inline ml-1" aria-hidden="true" />
                  הקשר השיחה:
                </p>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{cb.context}</p>
              </div>

              {cb.advisorNotes && (
                <div className="mb-3 bg-blue-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-blue-600 mb-1">הערות יועץ:</p>
                  <p className="text-sm text-blue-800">{cb.advisorNotes}</p>
                </div>
              )}

              {cb.resolvedAt && (
                <p className="text-xs text-gray-400">טופל: {formatDate(cb.resolvedAt)}</p>
              )}

              {cb.status === 'pending' && (
                <>
                  {resolvingId === cb._id ? (
                    <div className="mt-3 space-y-2">
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="הערות / תשובה ללקוח..."
                        aria-label="הערות / תשובה ללקוח"
                        className="input text-sm w-full"
                        rows={2}
                        style={{ fontSize: '16px' }}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleResolve(cb._id)}
                          className="btn btn-primary text-sm flex-1"
                        >
                          <CheckCircle size={14} aria-hidden="true" />
                          סמן כטופל
                        </button>
                        <button
                          onClick={() => { setResolvingId(null); setNotes('') }}
                          className="btn btn-outline text-sm"
                        >
                          ביטול
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => setResolvingId(cb._id)}
                        className="btn btn-primary text-sm"
                      >
                        <Phone size={14} aria-hidden="true" />
                        טפל בבקשה
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
